import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ModalTitle } from '@components/modal-title/modal-title';
import { ProspectService } from '@services/prospect-service';
import { StageColorService } from '@services/stage-color-service';
import { NotifyService } from '@intelishs-group/notify-service';
import type { VEtapaProspectoDTO, ProspectStageTimelineDTO } from '@schemas/prospect';

function getStatusTagColor(status: string | null | undefined): string {
  switch (status?.toLowerCase()) {
    case 'completado':
    case 'completada': return 'green';
    case 'en curso':
    case 'en progreso':
    case 'actual': return 'blue';
    case 'pendiente': return 'orange';
    case 'rechazado':
    case 'rechazo': return 'red';
    default: return 'default';
  }
}

@Component({
  selector: 'app-prospect-history-modal',
  standalone: true,
  imports: [
    NzModalModule,
    NzTimelineModule,
    NzCardModule,
    NzTagModule,
    NzButtonModule,
    NzIconModule,
    NzDividerModule,
    NzSpinModule,
    ModalTitle,
  ],
  templateUrl: './prospect-history-modal.html',
  styleUrl: './prospect-history-modal.css',
})
export class ProspectHistoryModal implements OnChanges {
  private prospectService = inject(ProspectService);
  private stageColorService = inject(StageColorService);
  private notifyService = inject(NotifyService);

  @Input() visible = false;
  @Input() prospect: VEtapaProspectoDTO | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  timeline: ProspectStageTimelineDTO[] = [];
  loading = false;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['visible'] || changes['prospect']) && this.visible && this.prospect) {
      this.loadTimeline();
    }
  }

  private async loadTimeline(): Promise<void> {
    if (!this.prospect?.fkIdProspecto) return;
    this.loading = true;
    try {
      await this.stageColorService.ensureLoaded();
      const result = await this.prospectService.stageTimeline(this.prospect.fkIdProspecto);
      this.timeline = result ?? [];
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al cargar el historial';
      this.notifyService.errorNotyfy(msg);
      this.timeline = [];
    } finally {
      this.loading = false;
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  getStageColor(stageName: string | null | undefined, status?: string | null): string {
    const completed = ['completado', 'completada'].includes(status?.toLowerCase() ?? '');
    if (!completed || !stageName) return '#d9d9d9';
    return this.stageColorService.getByName(stageName);
  }

  getTagColor(status: string | null | undefined): string {
    return getStatusTagColor(status);
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
